import React, { useEffect, useState } from "react";
import { Card } from "./TvBits";
import { channelVideosSeen } from "../../lib/api";

const CALL = { buy: "#10B981", watch: "#38BDF8", sell: "#EF4444", avoid: "#F97316" };
const SHOWN = new Set(); // shown this session (server also marks seen_on_tv)
export const unseenVideos = (d) => (d.channel_videos || []).filter((v) => !SHOWN.has(v.video_id));
const ROTATE = 12000;
export const fmtTime = (s) => (s ? new Date(s).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) + " CT" : "");

const Calls = ({ rows, label, color }) => rows?.length ? (
  <div className="space-y-2">
    <p className="nx-mono text-xs tracking-widest" style={{ color }}>{label}</p>
    {rows.slice(0, 6).map((p, i) => (
      <div key={i} className="flex items-center gap-3 text-base">
        <b className="font-mono text-cyan-50 w-16 shrink-0">{p.ticker}</b>
        <span className="nx-orbitron text-xs font-black tracking-widest px-2 py-0.5 rounded-md w-16 shrink-0 text-center text-[#030712]" style={{ background: CALL[p.call] || color }}>{(p.call || "").toUpperCase()}</span>
        <span className="text-sm text-cyan-100/80 line-clamp-2">{p.reason}</span>
      </div>
    ))}
  </div>
) : null;

export const TvChannelVideos = ({ d, items, onDone, replay, paused, skip }) => {
  const [list] = useState(() => items || unseenVideos(d));
  const [i, setI] = useState(0);
  const v = list[i];
  const done = React.useRef(onDone);
  done.current = onDone;
  useEffect(() => {
    if (!v) return;
    SHOWN.add(v.video_id);
    if (!replay) channelVideosSeen([v.video_id]).catch(() => {});
    if (paused) return;
    const t = setTimeout(() => (i + 1 < list.length ? setI(i + 1) : done.current && done.current()), ROTATE);
    return () => clearTimeout(t);
  }, [v, i, list.length, replay, paused]);
  const first = React.useRef(skip);
  useEffect(() => {  // peace-sign gesture: jump to the next video now
    if (skip === first.current) return;
    first.current = skip;
    setI((k) => { if (k + 1 < list.length) return k + 1; done.current && done.current(); return k; });
  }, [skip]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!v) return null;
  return (
    <div className="grid grid-cols-5 gap-5 h-full" data-testid="tv-scene-channels">
      <Card title={`${paused ? "⏸ PAUSED · " : ""}${replay ? "REPLAY · " : ""}${v.channel.toUpperCase()} · ${i + 1}/${list.length}`} accent="#A78BFA" code={v.topic?.toUpperCase()} className="col-span-3" testid="tv-chv-main">
        <p className="nx-mono text-sm text-violet-200/80 mb-1" data-testid="tv-chv-channel">{v.channel} · scraped {fmtTime(v.scraped_at)}</p>
        <p className="text-[clamp(1rem,1.5vw,1.5rem)] font-black text-white leading-tight line-clamp-2" data-testid="tv-chv-title">{v.title}</p>
        <p className="text-base text-violet-200 mt-1 mb-2" data-testid="tv-chv-topic">{v.topic}</p>
        {v.takeaway && <p className="text-base text-white/90 font-semibold leading-snug mb-3 border-l-4 border-emerald-400/70 pl-3" data-testid="tv-chv-takeaway">{v.takeaway}</p>}
        {(v.facts || []).length > 0 && (
          <div className="space-y-1 mb-3" data-testid="tv-chv-facts">
            <p className="nx-mono text-xs tracking-widest text-cyan-300/70">KEY FACTS</p>
            {v.facts.slice(0, 8).map((f, k) => <p key={k} className="text-sm text-cyan-50/90 leading-snug">• {f}</p>)}
          </div>)}
        <div className="space-y-3">
          <Calls rows={v.picks} label="PICKS" color="#10B981" />
          <Calls rows={v.rejects} label="REJECTS" color="#EF4444" />
        </div>
      </Card>
      <div className="col-span-2 flex flex-col gap-5 min-h-0">
        <div className="relative rounded-3xl overflow-hidden shrink-0 border border-violet-300/30 h-[28%]" data-testid="tv-chv-video">
          <img src={v.thumbnail} alt="" className="w-full h-full object-cover" />
        </div>
        <Card title="SUMMARY" accent="#A78BFA" code={`${(v.picks || []).length} PICKS · ${(v.rejects || []).length} REJECTS`} className="flex-1" testid="tv-chv-summary">
          <div className="space-y-2">{(v.summary || []).slice(0, 10).map((t, k) => <p key={k} className="text-sm text-cyan-50 leading-snug border-l-2 border-violet-400/60 pl-2">{t}</p>)}</div>
        </Card>
      </div>
    </div>
  );
};

// TV A: channel videos all day — new ones first, then replay of the last 24h; refetches after each pass.
export const TvChannelFeed = () => {
  const [feed, setFeed] = useState(null);
  const [pass, setPass] = useState(0);
  useEffect(() => {
    let t;
    const load = () => import("../../lib/api").then(({ channelVideosFeed }) => channelVideosFeed())
      .then((f) => { if (f.items.length) setFeed(f); else t = setTimeout(load, 60000); })
      .catch(() => { t = setTimeout(load, 60000); });
    load();
    return () => clearTimeout(t);
  }, [pass]);
  const [paused, setPaused] = useState(false);
  const [skip, setSkip] = useState(0);
  useEffect(() => {  // gestures from /eye: Victory = next video, Open_Palm = pause / resume
    let last = null, stop = false;
    const poll = () => import("../../lib/api").then(({ eyeGet }) => eyeGet("master")).then((e) => {
      const c = e?.tv_cmd;
      if (c && c.id !== last) {
        if (last !== null && Date.now() / 1000 - c.ts < 20) { if (c.kind === "tv_next") setSkip((x) => x + 1); else setPaused((p) => !p); }
        last = c.id;
      } else if (!c && last === null) last = "";
    }).catch(() => {}).finally(() => { if (!stop) setTimeout(poll, 1000); });
    poll();
    return () => { stop = true; };
  }, []);
  if (!feed) return <div className="h-full flex items-center justify-center nx-mono text-cyan-300/40 tracking-widest" data-testid="tv-a-feed-wait">WAITING FOR CHANNEL VIDEOS…</div>;
  return <TvChannelVideos key={pass} items={feed.items} replay={feed.replay} paused={paused} skip={skip} onDone={() => setPass((p) => p + 1)} />;
};
